import {
  Box,
  Divider,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import { ABOUT_SECTIONS } from '@/designs-dev/about/About.content';
import { CHROME_VH, CHROME_ZOOM } from '@/designs-dev/inspect/chrome';

export type AboutMenuProps = {
  onNavigate: (path: string) => void;
  onSignOut: () => void;
  onCollapse?: () => void;
  currentPath?: string;
};

// The About navigation: while you're in the About area the sidebar swaps from the
// prototype zones to this docs table of contents, so Loft can document itself.
// MenuLayout renders it in place of SideMenu for any /about route.
const AboutMenu = ({
  onNavigate,
  onSignOut,
  onCollapse,
  currentPath,
}: AboutMenuProps) => {
  // /about lands on the first section; /about/<slug> selects its own.
  const activeSlug =
    ABOUT_SECTIONS.find((s) => currentPath === `/about/${s.slug}`)?.slug ??
    ABOUT_SECTIONS[0].slug;

  return (
    <Paper
      square
      sx={{
        // Docked beside the page from md up. On a phone it would push the page
        // sideways, so it slides over it instead, at most most of the width.
        width: { xs: 'min(280px, 85vw)', md: 280 },
        flexShrink: 0,
        zoom: CHROME_ZOOM,
        height: CHROME_VH(100),
        position: { xs: 'fixed', md: 'sticky' },
        top: 0,
        left: 0,
        zIndex: { xs: 1250, md: 'auto' },
        borderRight: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 2,
        }}
        direction='row'
      >
        <Typography
          component='button'
          variant='h5'
          onClick={() => onNavigate('/')}
          sx={{
            border: 'none',
            bgcolor: 'transparent',
            cursor: 'pointer',
            p: 0,
          }}
        >
          Loft
        </Typography>
        {onCollapse && (
          <IconButton
            size='small'
            onClick={onCollapse}
            aria-label='Collapse menu'
          >
            <Icon icon='chevrons-left' fontSize={16} />
          </IconButton>
        )}
      </Stack>
      <Divider />

      {/* Back to the prototype nav. */}
      <MenuList dense disablePadding>
        <MenuItem onClick={() => onNavigate('/')}>
          <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1}>
            <Box sx={{ display: 'flex', color: 'text.disabled' }}>
              <Icon icon='chevron-left' fontSize={12} />
            </Box>
            <Typography variant='body2'>Back to Loft</Typography>
          </Stack>
        </MenuItem>
      </MenuList>

      <Box sx={{ px: 2, pt: 2, pb: 0.5 }}>
        <Typography variant='overline'>About</Typography>
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        <MenuList dense disablePadding>
          {ABOUT_SECTIONS.map((s) => (
            <MenuItem
              key={s.slug}
              selected={s.slug === activeSlug}
              onClick={() => onNavigate(`/about/${s.slug}`)}
            >
              {s.title}
            </MenuItem>
          ))}
        </MenuList>
      </Box>

      <Divider />
      <MenuList>
        <MenuItem onClick={onSignOut}>Sign out</MenuItem>
      </MenuList>
    </Paper>
  );
};

export default AboutMenu;
